import React from 'react';

// Spinner component with different sizes
export const Spinner: React.FC<{
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'white' | 'secondary';
  className?: string;
}> = ({ size = 'md', color = 'primary', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  const colorClasses = {
    primary: 'border-cardboard',
    white: 'border-white',
    secondary: 'border-urban'
  };

  return (
    <div
      className={`animate-spin rounded-full border-2 border-t-transparent ${sizeClasses[size]} ${colorClasses[color]} ${className}`}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
};

// Loading button component
export const LoadingButton: React.FC<{
  isLoading: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  type?: 'button' | 'submit' | 'reset';
}> = ({
  isLoading,
  children,
  onClick,
  disabled = false,
  variant = 'primary',
  size = 'md',
  className = '',
  type = 'button'
}) => {
  const baseClasses = 'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    primary: 'bg-cardboard hover:bg-cardboard-600 text-white hover:shadow-lg hover:-translate-y-0.5',
    secondary: 'bg-white border-2 border-cardboard text-cardboard hover:bg-cardboard hover:text-white',
    ghost: 'bg-transparent text-cardboard hover:bg-cardboard-50'
  };

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm touch-target',
    md: 'px-4 py-3 touch-target',
    lg: 'px-6 py-4 text-lg touch-target'
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {isLoading && (
        <Spinner 
          size={size === 'lg' ? 'md' : 'sm'} 
          color={variant === 'primary' ? 'white' : 'primary'} 
        />
      )}
      <span className={isLoading ? 'opacity-70' : ''}>
        {children}
      </span>
    </button>
  );
};

// Inline loading indicator
export const InlineLoading: React.FC<{
  text?: string;
  size?: 'sm' | 'md';
}> = ({ text = 'Loading...', size = 'md' }) => (
  <div className="flex items-center gap-2 text-urban-400">
    <Spinner size={size === 'sm' ? 'sm' : 'md'} color="secondary" />
    <span className={size === 'sm' ? 'text-sm' : ''}>{text}</span>
  </div>
);

// Full page loading overlay
export const LoadingOverlay: React.FC<{
  isVisible: boolean;
  text?: string;
  backdrop?: boolean;
}> = ({ isVisible, text = 'Loading...', backdrop = true }) => {
  if (!isVisible) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center mobile-padding ${
      backdrop ? 'bg-black/50' : ''
    }`}>
      <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-urban font-medium text-center">{text}</p>
        </div>
      </div>
    </div>
  );
};

// Progress bar component
export const ProgressBar: React.FC<{
  progress: number; // 0-100
  label?: string;
  showPercentage?: boolean;
  color?: 'primary' | 'success' | 'warning' | 'error';
  size?: 'sm' | 'md' | 'lg';
}> = ({ 
  progress, 
  label, 
  showPercentage = true, 
  color = 'primary',
  size = 'md'
}) => {
  const colorClasses = {
    primary: 'bg-cardboard',
    success: 'bg-verge',
    warning: 'bg-treasure',
    error: 'bg-rust'
  };

  const sizeClasses = {
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4'
  };

  const clampedProgress = Math.max(0, Math.min(100, progress));

  return (
    <div className="w-full">
      {(label || showPercentage) && (
        <div className="flex justify-between items-center mb-2">
          {label && <span className="text-sm font-medium text-urban">{label}</span>}
          {showPercentage && (
            <span className="text-sm text-urban-400">{Math.round(clampedProgress)}%</span>
          )}
        </div>
      )}
      <div className={`w-full bg-urban-100 rounded-full overflow-hidden ${sizeClasses[size]}`}>
        <div
          className={`${sizeClasses[size]} ${colorClasses[color]} transition-all duration-300 ease-out rounded-full`}
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
    </div>
  );
};

// Upload progress indicator
export const UploadProgress: React.FC<{
  files: Array<{
    name: string;
    progress: number;
    status: 'uploading' | 'completed' | 'error';
  }>;
  onCancel?: (fileName: string) => void;
}> = ({ files, onCancel }) => (
  <div className="space-y-3">
    {files.map((file) => (
      <div key={file.name} className="bg-white rounded-lg p-3 sm:p-4 border border-urban-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs sm:text-sm font-medium text-urban truncate flex-1 mr-2">
            {file.name}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {file.status === 'uploading' && (
              <Spinner size="sm" />
            )}
            {file.status === 'completed' && (
              <span className="text-verge text-sm">✓</span>
            )}
            {file.status === 'error' && (
              <span className="text-rust text-sm">✗</span>
            )}
            {onCancel && file.status === 'uploading' && (
              <button
                onClick={() => onCancel(file.name)}
                className="text-urban-400 hover:text-rust text-xs sm:text-sm touch-target px-2 py-1"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
        <ProgressBar
          progress={file.progress}
          color={
            file.status === 'completed' ? 'success' :
            file.status === 'error' ? 'error' : 'primary'
          }
          size="sm"
          showPercentage={false}
        />
      </div>
    ))}
  </div>
);

// Skeleton pulse animation (alternative to shimmer)
export const PulseLoader: React.FC<{
  className?: string;
  count?: number;
}> = ({ className = 'h-4 bg-urban-100 rounded', count = 1 }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        className={`animate-pulse ${className}`}
      />
    ))}
  </>
);

// Dots loading animation
export const DotsLoader: React.FC<{
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary';
}> = ({ size = 'md', color = 'primary' }) => {
  const sizeClasses = {
    sm: 'w-1 h-1',
    md: 'w-2 h-2',
    lg: 'w-3 h-3'
  };

  const colorClasses = {
    primary: 'bg-cardboard',
    secondary: 'bg-urban-400'
  };

  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full animate-pulse`}
          style={{
            animationDelay: `${index * 0.2}s`,
            animationDuration: '1s'
          }}
        />
      ))}
    </div>
  );
};

// Loading card for content that's being fetched
export const LoadingCard: React.FC<{
  title?: string;
  description?: string;
  className?: string;
}> = ({ 
  title = 'Loading content...', 
  description = 'Please wait while we fetch the latest information.',
  className = ''
}) => (
  <div className={`bg-white rounded-lg p-4 sm:p-6 shadow-card ${className}`}>
    <div className="flex items-center gap-3 mb-3 sm:mb-4">
      <Spinner size="md" />
      <h3 className="font-semibold text-urban text-sm sm:text-base">{title}</h3>
    </div>
    <p className="text-urban-400 text-xs sm:text-sm leading-relaxed">{description}</p>
  </div>
);

// Loading state for tables/lists
export const TableLoading: React.FC<{
  rows?: number;
  columns?: number;
}> = ({ rows = 5, columns = 4 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="flex gap-4">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <div
            key={colIndex}
            className="flex-1 h-4 bg-gradient-to-r from-teddy-100 via-teddy-200 to-teddy-100 animate-shimmer bg-[length:200%_100%] rounded"
          />
        ))}
      </div>
    ))}
  </div>
);